import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Copy, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface PromoCode {
  id: string;
  code: string;
  description: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  currency: string;
  min_purchase_amount: number;
  max_discount_amount: number | null;
  usage_limit: number | null;
  usage_count: number;
  start_date: string;
  end_date: string | null;
  active: boolean;
  created_at: string;
  product_id: string | null;
  category_id: string | null;
}

interface Product {
  id: string;
  title: string;
  category: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

export const PromoCodesManager = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [openProductCombo, setOpenProductCombo] = useState(false);
  const [openCategoryCombo, setOpenCategoryCombo] = useState(false);
  const [scopeType, setScopeType] = useState<'all' | 'product' | 'category'>('all');

  // Form state
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed_amount'>('percentage');
  const [discountValue, setDiscountValue] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [minPurchase, setMinPurchase] = useState("");
  const [maxDiscount, setMaxDiscount] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState("");
  const [active, setActive] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

  useEffect(() => {
    fetchPromoCodes();
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPromoCodes((data || []) as PromoCode[]);
    } catch (error: any) {
      console.error('Error fetching promo codes:', error);
      toast.error('Erreur lors du chargement des codes promo');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, title, category')
        .eq('active', true)
        .order('title');

      if (error) throw error;
      setProducts(data || []);
    } catch (error: any) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, slug')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error: any) {
      console.error('Error fetching categories:', error);
    }
  };

  const resetForm = () => {
    setCode("");
    setDescription("");
    setDiscountType('percentage');
    setDiscountValue("");
    setCurrency("USD");
    setMinPurchase("");
    setMaxDiscount("");
    setUsageLimit("");
    setStartDate(new Date().toISOString().split('T')[0]);
    setEndDate("");
    setActive(true);
    setScopeType('all');
    setSelectedProductId("");
    setSelectedCategoryId("");
    setEditingPromo(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const promoData = {
        code: code.toUpperCase(),
        description,
        discount_type: discountType,
        discount_value: parseFloat(discountValue),
        currency,
        min_purchase_amount: minPurchase ? parseFloat(minPurchase) : 0,
        max_discount_amount: maxDiscount ? parseFloat(maxDiscount) : null,
        usage_limit: usageLimit ? parseInt(usageLimit) : null,
        start_date: new Date(startDate).toISOString(),
        end_date: endDate ? new Date(endDate).toISOString() : null,
        active,
        product_id: scopeType === 'product' ? selectedProductId || null : null,
        category_id: scopeType === 'category' ? selectedCategoryId || null : null,
      };

      if (editingPromo) {
        const { error } = await supabase
          .from('promo_codes')
          .update(promoData)
          .eq('id', editingPromo.id);

        if (error) throw error;
        toast.success('Code promo mis à jour');
      } else {
        const { error } = await supabase
          .from('promo_codes')
          .insert(promoData);

        if (error) throw error;
        toast.success('Code promo créé');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchPromoCodes();
    } catch (error: any) {
      console.error('Error saving promo code:', error);
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (promo: PromoCode) => {
    setEditingPromo(promo);
    setCode(promo.code);
    setDescription(promo.description);
    setDiscountType(promo.discount_type);
    setDiscountValue(promo.discount_value.toString());
    setCurrency(promo.currency);
    setMinPurchase(promo.min_purchase_amount.toString());
    setMaxDiscount(promo.max_discount_amount?.toString() || "");
    setUsageLimit(promo.usage_limit?.toString() || "");
    setStartDate(new Date(promo.start_date).toISOString().split('T')[0]);
    setEndDate(promo.end_date ? new Date(promo.end_date).toISOString().split('T')[0] : "");
    setActive(promo.active);
    
    if (promo.product_id) {
      setScopeType('product');
      setSelectedProductId(promo.product_id);
    } else if (promo.category_id) {
      setScopeType('category');
      setSelectedCategoryId(promo.category_id);
    } else {
      setScopeType('all');
    }
    
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce code promo ?')) return;

    try {
      const { error } = await supabase
        .from('promo_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Code promo supprimé');
      fetchPromoCodes();
    } catch (error: any) {
      console.error('Error deleting promo code:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copié dans le presse-papier');
  };

  if (loading) {
    return <div className="p-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Codes Promotionnels</h2>
          <p className="text-muted-foreground">Gérez les codes promo et réductions</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-primary">
              <Plus className="mr-2 h-4 w-4" />
              Nouveau code promo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPromo ? 'Modifier' : 'Créer'} un code promo</DialogTitle>
              <DialogDescription>
                Configurez les paramètres du code promotionnel
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="PROMO2025"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="discountType">Type de réduction *</Label>
                  <Select value={discountType} onValueChange={(value: any) => setDiscountType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Pourcentage</SelectItem>
                      <SelectItem value="fixed_amount">Montant fixe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Promotion de printemps"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="discountValue">
                    Valeur * {discountType === 'percentage' ? '(%)' : `(${currency})`}
                  </Label>
                  <Input
                    id="discountValue"
                    type="number"
                    step="0.01"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    required
                  />
                </div>
                {discountType === 'fixed_amount' && (
                  <div className="space-y-2">
                    <Label htmlFor="currency">Devise</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="CDF">CDF</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="minPurchase">Montant minimum d'achat</Label>
                  <Input
                    id="minPurchase"
                    type="number"
                    step="0.01"
                    value={minPurchase}
                    onChange={(e) => setMinPurchase(e.target.value)}
                    placeholder="0"
                  />
                </div>
                {discountType === 'percentage' && (
                  <div className="space-y-2">
                    <Label htmlFor="maxDiscount">Réduction maximale</Label>
                    <Input
                      id="maxDiscount"
                      type="number"
                      step="0.01"
                      value={maxDiscount}
                      onChange={(e) => setMaxDiscount(e.target.value)}
                      placeholder="Illimité"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="usageLimit">Limite d'utilisation</Label>
                <Input
                  id="usageLimit"
                  type="number"
                  value={usageLimit}
                  onChange={(e) => setUsageLimit(e.target.value)}
                  placeholder="Illimité"
                />
              </div>

              <div className="space-y-4">
                <Label>Portée du code promo</Label>
                <RadioGroup value={scopeType} onValueChange={(value: any) => {
                  setScopeType(value);
                  if (value === 'all') {
                    setSelectedProductId("");
                    setSelectedCategoryId("");
                  }
                }}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="scope-all" />
                    <Label htmlFor="scope-all" className="font-normal cursor-pointer">
                      Tous les produits
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="product" id="scope-product" />
                    <Label htmlFor="scope-product" className="font-normal cursor-pointer">
                      Un produit spécifique
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="category" id="scope-category" />
                    <Label htmlFor="scope-category" className="font-normal cursor-pointer">
                      Une catégorie spécifique
                    </Label>
                  </div>
                </RadioGroup>

                {scopeType === 'product' && (
                  <div className="space-y-2">
                    <Label>Sélectionner un produit</Label>
                    <Popover open={openProductCombo} onOpenChange={setOpenProductCombo}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openProductCombo}
                          className="w-full justify-between"
                        >
                          {selectedProductId
                            ? products.find((p) => p.id === selectedProductId)?.title
                            : "Rechercher un produit..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Rechercher un produit..." />
                          <CommandEmpty>Aucun produit trouvé.</CommandEmpty>
                          <CommandGroup className="max-h-64 overflow-auto">
                            {products.map((product) => (
                              <CommandItem
                                key={product.id}
                                value={`${product.title} ${product.id}`}
                                onSelect={() => {
                                  setSelectedProductId(product.id);
                                  setOpenProductCombo(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedProductId === product.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {product.title}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {scopeType === 'category' && (
                  <div className="space-y-2">
                    <Label>Sélectionner une catégorie</Label>
                    <Popover open={openCategoryCombo} onOpenChange={setOpenCategoryCombo}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openCategoryCombo}
                          className="w-full justify-between"
                        >
                          {selectedCategoryId
                            ? categories.find((c) => c.id === selectedCategoryId)?.name
                            : "Rechercher une catégorie..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Rechercher une catégorie..." />
                          <CommandEmpty>Aucune catégorie trouvée.</CommandEmpty>
                          <CommandGroup className="max-h-64 overflow-auto">
                            {categories.map((category) => (
                              <CommandItem
                                key={category.id}
                                value={`${category.name} ${category.id}`}
                                onSelect={() => {
                                  setSelectedCategoryId(category.id);
                                  setOpenCategoryCombo(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedCategoryId === category.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {category.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Date de début *</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">Date de fin</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="rounded border-input"
                />
                <Label htmlFor="active">Code actif</Label>
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-gradient-primary">
                  {editingPromo ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Codes promo actifs</CardTitle>
          <CardDescription>{promoCodes.length} code(s) promo au total</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Réduction</TableHead>
                <TableHead>Utilisation</TableHead>
                <TableHead>Validité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {promoCodes.map((promo) => (
                <TableRow key={promo.id}>
                  <TableCell className="font-mono font-bold">
                    {promo.code}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2"
                      onClick={() => copyCode(promo.code)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </TableCell>
                  <TableCell>{promo.description}</TableCell>
                  <TableCell>
                    {promo.discount_type === 'percentage'
                      ? `${promo.discount_value}%`
                      : `${promo.discount_value} ${promo.currency}`}
                  </TableCell>
                  <TableCell>
                    {promo.usage_count} / {promo.usage_limit || '∞'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {new Date(promo.start_date).toLocaleDateString()}
                    {promo.end_date && ` - ${new Date(promo.end_date).toLocaleDateString()}`}
                  </TableCell>
                  <TableCell>
                    <Badge variant={promo.active ? 'default' : 'secondary'}>
                      {promo.active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(promo)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(promo.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
